import { useState, useEffect, useRef } from 'react';
import { Loader } from '@googlemaps/js-api-loader';

const useLocationSearch = () => {
  const [searchText, setSearchText] = useState('');
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [isApiLoaded, setIsApiLoaded] = useState(false);
  
  // Keep a ref to the autocomplete service
  const autocompleteService = useRef(null);
  const placesService = useRef(null);
  
  // Initialize Google Places services dynamically
  useEffect(() => {
    const loadGoogleMapsAPI = async () => {
      try {
        const loader = new Loader({
          apiKey: process.env.REACT_APP_GOOGLE_API_KEY,
          version: 'weekly',
          libraries: ['places']
        });

        await loader.load();
        
        if (window.google && window.google.maps && window.google.maps.places) {
          autocompleteService.current = new window.google.maps.places.AutocompleteService();
          placesService.current = new window.google.maps.places.PlacesService(
            document.createElement('div')
          );
          setIsApiLoaded(true);
        }
      } catch (error) {
        setError(error);
        console.error('Error loading Google Maps API:', error);
      }
    };

    // Check if API is already loaded (from other components)
    if (window.google && window.google.maps && window.google.maps.places) {
      autocompleteService.current = new window.google.maps.places.AutocompleteService();
      placesService.current = new window.google.maps.places.PlacesService(
        document.createElement('div')
      );
      setIsApiLoaded(true);
    } else {
      loadGoogleMapsAPI();
    }
  }, []);
  
  // Handle search text changes
  const searchLocations = (text) => {
    setSearchText(text);
    
    if (!text || text.trim().length < 2) {
      setLocations([]);
      return;
    }

    if (!isApiLoaded || !autocompleteService.current) {
      setError(new Error('Google Places API not loaded yet'));
      return;
    }
    
    setLoading(true);
    autocompleteService.current.getPlacePredictions(
      {
        input: text,
        componentRestrictions: { country: 'de' },
        types: ['geocode', 'establishment']
      },
      (predictions, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && predictions) {
          const formattedLocations = predictions.map(prediction => ({
            id: prediction.place_id,
            name: prediction.description,
            placeId: prediction.place_id
          }));
          setLocations(formattedLocations);
        } else {
          setLocations([]);
        }
        setLoading(false);
      }
    );
  };

  // Handle location selection
  const selectLocation = (placeId) => {
    if (!isApiLoaded || !placesService.current) {
      setError(new Error('Google Places API not loaded yet'));
      return;
    }
    
    setLoading(true);
    placesService.current.getDetails(
      { placeId, fields: ['geometry', 'formatted_address', 'name'] },
      (place, status) => {
        if (status === window.google.maps.places.PlacesServiceStatus.OK && place) {
          const locationData = {
            id: placeId,
            name: place.formatted_address || place.name,
            latitude: place.geometry.location.lat(),
            longitude: place.geometry.location.lng(),
            address: place.formatted_address
          };
          setSelectedLocation(locationData);
          setSearchText(locationData.name);
        } else {
          setError(new Error('Failed to get location details'));
        }
        setLoading(false);
      }
    );
  };
  
  return {
    searchText,
    locations,
    loading,
    error,
    selectedLocation,
    isApiLoaded,
    searchLocations,
    selectLocation,
    clearLocation: () => {
      setSelectedLocation(null);
      setSearchText('');
    }
  };
};

export default useLocationSearch;